/**
 * Auth service API client - wires frontend to auth-service backend.
 *
 * Handles LINE Login flow, session management, and verification.
 *
 * Requirements: 21.1, 21.2, 21.3, 21.4, 21.5, 21.6, 21.7
 */

import apiClient from './api';

/**
 * Last-resort Terms of Service version used only when the active version cannot
 * be resolved from the backend. MUST track the registry's current active
 * version (`@findoryhub/tos`). Kept in one place so the ToS gate and the
 * acceptance page never drift.
 */
export const TOS_FALLBACK_VERSION = '1.1.0';

// --- Types ---

export interface SessionInfo {
  userId: string;
  displayName: string;
  profileImageUrl?: string;
  verificationStatus: string;
  accountRiskStatus: string;
  primaryUniversityId?: string;
  primaryUniversityName?: string;
  role: 'user' | 'admin';
  acceptedTosVersion?: string;
  phoneContactField?: string | null;
  phoneContactVerifiedAt?: string | null;
  hasPhoneContact?: boolean;
  lineContactField?: string | null;
  hasLineContact?: boolean;
  // The /auth/session endpoint can surface additional dynamic fields beyond
  // the ones enumerated above. Allow indexed access so consumers can read
  // them without widening the whole value to `any`.
  [key: string]: unknown;
}

export interface LoginResult {
  sessionToken: string;
  user: SessionInfo;
  isNewUser: boolean;
}

// --- Token storage ---

let sessionToken: string | null = null;

function getToken(): string | null {
  if (!sessionToken) {
    sessionToken = localStorage.getItem('findoryhub_session_token') || localStorage.getItem('findoryhub_session');
  }
  return sessionToken;
}

function setToken(token: string | null): void {
  sessionToken = token;
  if (token) {
    localStorage.setItem('findoryhub_session_token', token);
    localStorage.setItem('findoryhub_session', token);
  } else {
    localStorage.removeItem('findoryhub_session_token');
    localStorage.removeItem('findoryhub_session');
  }
}

// Token handlers are registered by AuthContext during render.
// The auth.ts getToken helper is kept as a fallback for direct usage.

// --- API Functions ---

/**
 * Initiates LINE Login by redirecting to LINE OAuth.
 * Stores the current URL for post-login redirect.
 */
export function initiateLineLogin(redirectPath?: string): void {
  if (redirectPath) {
    sessionStorage.setItem('findoryhub_redirect', redirectPath);
  }
  const loginUrl = `${apiClient.defaults.baseURL}/auth/line/login`;
  window.location.href = loginUrl;
}

/**
 * Completes LINE Login callback.
 * Called after LINE redirects back with authorization code.
 */
export async function completeLineLogin(code: string, state: string): Promise<LoginResult> {
  const response = await apiClient.post<LoginResult>('/auth/line/callback', {
    code,
    state,
    redirectUri: `${window.location.origin}/auth/callback`,
  });
  setToken(response.data.sessionToken);
  return response.data;
}

/**
 * Gets current session info.
 */
export async function getSession(): Promise<SessionInfo | null> {
  const token = getToken();
  if (!token) return null;

  try {
    const response = await apiClient.get('/auth/session');
    // The backend returns { user: { ... }, activeTosVersion, ... }
    // Extract the user object which matches SessionInfo
    return response.data?.user ?? response.data;
  } catch {
    setToken(null);
    return null;
  }
}

/**
 * Logs out the current user.
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout');
  } finally {
    setToken(null);
  }
}

/**
 * Accepts Terms of Service.
 */
export async function acceptTos(version: string): Promise<void> {
  await apiClient.post('/auth/tos/accept', { version });
}

/**
 * Resolves the currently active ToS version from the PUBLIC `GET /tos/active`
 * endpoint (no auth header required). Returns `null` on failure so callers can
 * retry or fall back to {@link TOS_FALLBACK_VERSION}.
 *
 * This is the reliable source for the ToS gate: it does not depend on
 * `GET /auth/session` (which can fail silently and leave the gate comparing
 * against a stale fallback, causing re-acceptance prompts to be skipped on a
 * version bump).
 */
export async function getActiveTosVersion(): Promise<string | null> {
  try {
    const response = await apiClient.get<{ version?: string }>('/tos/active');
    return response.data?.version ?? null;
  } catch {
    return null;
  }
}

/**
 * Sends email verification code.
 * @param email - The email address to verify
 * @param type - Either 'general' (personal email) or 'university' (university email)
 * @param marketingOptIn - Whether the user agreed to receive partner offers
 *                        and promotional emails (PDPA: explicit opt-in only)
 */
export async function sendVerificationEmail(
  email: string,
  type: 'general' | 'university' = 'university',
  marketingOptIn: boolean = false,
): Promise<void> {
  await apiClient.post('/auth/verify-email/send', { email, type, marketingOptIn });
}

/**
 * Confirms university email verification code.
 */
export async function confirmVerificationEmail(code: string): Promise<void> {
  await apiClient.post('/auth/verify-email/confirm', { code });
}

// --- Phone Verification ---

export interface SendOtpResult {
  sessionId: string;
  expiresAt: string;
  maskedPhone: string;
}

export interface VerifyOtpResult {
  verified: boolean;
  phone?: string;
  phoneContactVerifiedAt?: string;
  maskedPhone?: string;
  remaining?: number;
  exhausted?: boolean;
}

/**
 * Sends a phone OTP to the given Thai mobile number.
 * POST /phone/send-otp
 */
export async function sendPhoneOtp(phoneNumber: string): Promise<SendOtpResult> {
  const response = await apiClient.post<SendOtpResult>('/phone/send-otp', { phoneNumber });
  return response.data;
}

/**
 * Verifies a phone OTP code for an active session.
 * POST /phone/verify-otp
 */
export async function verifyPhoneOtp(sessionId: string, code: string): Promise<VerifyOtpResult> {
  const response = await apiClient.post<VerifyOtpResult>('/phone/verify-otp', { sessionId, code });
  return response.data;
}

/**
 * Cancels an active phone OTP session (best-effort).
 * POST /phone/cancel
 */
export async function cancelPhoneOtp(sessionId: string): Promise<void> {
  await apiClient.post('/phone/cancel', { sessionId });
}

/**
 * Removes the user's verified phone number.
 * DELETE /phone
 */
export async function removePhone(): Promise<void> {
  await apiClient.delete('/phone');
}

/**
 * Gets the stored redirect path after login.
 */
export function getPostLoginRedirect(): string | null {
  const path = sessionStorage.getItem('findoryhub_redirect');
  sessionStorage.removeItem('findoryhub_redirect');
  return path;
}

/**
 * Checks if the user is currently authenticated.
 */
export function isAuthenticated(): boolean {
  return getToken() !== null;
}
